import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/fsm';

const EquipmentForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const f = t('fsm');
  const isRTL = lang === 'ar';

  const [form, setForm] = useState({
    client_id: '', location_id: '', category_id: '', name: '', name_ar: '',
    brand: '', model: '', serial_number: '', installation_date: '', warranty_expiry: '',
    status: 'active', floor: '', unit_number: '', notes: ''
  });
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDrop, setShowClientDrop] = useState(false);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const h = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    const fetchInit = async () => {
      try {
        const [catRes, cliRes] = await Promise.all([
          fetch(`${API}/equipment-categories`, { headers: h }),
          fetch(`${API}/clients?limit=200`, { headers: h }),
        ]);
        if (catRes.ok) { const d = await catRes.json(); if (d.success) setCategories(d.data); }
        if (cliRes.ok) { const d = await cliRes.json(); if (d.success) setClients(d.data); }
      } catch (err) { console.error(err); }
    };
    fetchInit();
    if (isEdit) {
      const fetchEq = async () => {
        try {
          const res = await fetch(`${API}/equipment/${id}`, { headers: h });
          const data = await res.json();
          if (data.success) {
            const eq = data.data;
            setForm({
              client_id: eq.client_id || '', location_id: eq.location_id || '',
              category_id: eq.category_id || '', name: eq.name || '', name_ar: eq.name_ar || '',
              brand: eq.brand || '', model: eq.model || '', serial_number: eq.serial_number || '',
              installation_date: eq.installation_date ? eq.installation_date.split('T')[0] : '',
              warranty_expiry: eq.warranty_expiry ? eq.warranty_expiry.split('T')[0] : '',
              status: eq.status || 'active', floor: eq.floor || '', unit_number: eq.unit_number || '',
              notes: eq.notes || ''
            });
          }
        } catch (err) { console.error(err); }
      };
      fetchEq();
    }
  }, [id]);

  useEffect(() => {
    if (!form.client_id) { setLocations([]); return; }
    const token = localStorage.getItem('token');
    const fetchLoc = async () => {
      try {
        const res = await fetch(`${API}/clients/${form.client_id}/locations`, { headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        if (data.success) setLocations(data.data);
      } catch (err) { console.error(err); }
    };
    fetchLoc();
  }, [form.client_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.client_id || !form.category_id || !form.name) {
      setError(f.required_fields || 'Client, Category, and Equipment Name are required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      payload.client_id = parseInt(form.client_id);
      payload.category_id = parseInt(form.category_id);
      payload.name = form.name;
      if (form.location_id) payload.location_id = parseInt(form.location_id);

      const url = isEdit ? `${API}/equipment/${id}` : `${API}/equipment`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        navigate(isEdit ? `/fsm/equipment/${id}` : '/fsm/equipment');
      } else {
        setError(data.error || (f.save_failed || 'Failed to save'));
      }
    } catch (err) { setError(f.network_err || 'Network error'); }
    setSaving(false);
  };

  const statusLabels = {
    active: f.active || 'Active', inactive: f.inactive || 'Inactive',
    maintenance: f.maintenance || 'Maintenance', retired: f.retired || 'Retired'
  };

  const inputSt = {
    width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box'
  };

  const labelSt = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#94a3b8', marginBottom: '6px' };

  const sectionSt = {
    backgroundColor: 'rgba(26, 32, 56, 0.8)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px', padding: '20px', marginBottom: '16px'
  };

  return (
    <Layout>
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', fontSize: '13px', color: '#94a3b8' }}>
        <span style={{ cursor: 'pointer', color: '#818cf8' }} onClick={() => navigate('/fsm/equipment')}>{f.equipment || 'Equipment'}</span>
        <span>/</span>
        <span>{isEdit ? (f.edit || 'Edit') : (f.new_equipment || 'New Equipment')}</span>
      </div>

      <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#f1f5f9', margin: '0 0 24px' }}>
        {isEdit ? `✏️ ${f.edit_equipment || 'Edit Equipment'}` : `➕ ${f.new_equipment || 'Register New Equipment'}`}
      </h1>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#f87171', fontSize: '14px' }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Client & Location */}
        <div style={sectionSt}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#818cf8', margin: '0 0 16px' }}>📍 {f.client_location || 'Client & Location'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelSt}>{f.client || 'Client'} <span style={{ color: '#f87171' }}>*</span></label>
              <div style={{position:'relative'}}>
                <input type="text" style={inputSt} autoComplete="off"
                  placeholder={f.select_client || 'Search client...'}
                  value={clientSearch || (form.client_id ? (clients.find(c=>String(c.id)===String(form.client_id))?.[isRTL?'name_ar':'name']||clients.find(c=>String(c.id)===String(form.client_id))?.name||'') : '')}
                  onChange={(e) => { setClientSearch(e.target.value); setShowClientDrop(true); if(!e.target.value){handleChange('client_id','');handleChange('location_id','');} }}
                  onFocus={() => setShowClientDrop(true)}
                    onBlur={() => setTimeout(() => setShowClientDrop(false), 200)}
                />
                {showClientDrop && (
                  <div style={{position:'absolute',top:'100%',left:0,right:0,maxHeight:'220px',overflowY:'auto',background:'#1e293b',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'0 0 8px 8px',zIndex:50}}>
                    {clients.filter(c=>{if(!clientSearch)return true;const s=clientSearch.toLowerCase();return(c.name||'').toLowerCase().includes(s)||(c.name_ar||'').toLowerCase().includes(s)||(c.company_name||'').toLowerCase().includes(s);}).map(c => (
                      <div key={c.id} onClick={()=>{handleChange('client_id',String(c.id));handleChange('location_id','');setClientSearch(isRTL&&c.name_ar?c.name_ar:(c.name||c.company_name));setShowClientDrop(false);}}
                        style={{padding:'9px 14px',cursor:'pointer',borderBottom:'1px solid rgba(255,255,255,0.05)',color:'#e2e8f0',fontSize:'13px'}}
                        onMouseEnter={e=>e.target.style.background='rgba(59,130,246,0.15)'}
                        onMouseLeave={e=>e.target.style.background='transparent'}>
                        {isRTL && c.name_ar ? c.name_ar : (c.name || c.company_name)}
                      </div>
                    ))}
                    {clients.filter(c=>{if(!clientSearch)return true;const s=clientSearch.toLowerCase();return(c.name||'').toLowerCase().includes(s)||(c.name_ar||'').toLowerCase().includes(s);}).length===0 && (
                      <div style={{padding:'12px',color:'#64748b',textAlign:'center',fontSize:'13px'}}>No clients found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label style={labelSt}>{f.location || 'Location'}</label>
              <select value={form.location_id} onChange={(e) => handleChange('location_id', e.target.value)} style={inputSt} disabled={!form.client_id}>
                <option value="">{f.select_location || 'Select location (optional)...'}</option>
                {locations.map(l => <option key={l.id} value={l.id}>{isRTL && l.name_ar ? l.name_ar : l.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
            <div>
              <label style={labelSt}>{f.floor || 'Floor'}</label>
              <input type="text" value={form.floor} onChange={(e) => handleChange('floor', e.target.value)} style={inputSt} placeholder={f.floor_placeholder || 'e.g. 3'} />
            </div>
            <div>
              <label style={labelSt}>{f.unit || 'Unit / Room'}</label>
              <input type="text" value={form.unit_number} onChange={(e) => handleChange('unit_number', e.target.value)} style={inputSt} placeholder={f.unit_placeholder || 'e.g. 301'} />
            </div>
          </div>
        </div>

        {/* Equipment Details */}
        <div style={sectionSt}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#818cf8', margin: '0 0 16px' }}>🔧 {f.equipment_info || 'Equipment Details'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelSt}>{f.category || 'Category'} <span style={{ color: '#f87171' }}>*</span></label>
              <select value={form.category_id} onChange={(e) => handleChange('category_id', e.target.value)} style={inputSt} required>
                <option value="">{f.select_category || 'Select category...'}</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {isRTL && c.name_ar ? c.name_ar : c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelSt}>{f.status || 'Status'}</label>
              <select value={form.status} onChange={(e) => handleChange('status', e.target.value)} style={inputSt}>
                <option value="active">{statusLabels.active}</option>
                <option value="inactive">{statusLabels.inactive}</option>
                <option value="maintenance">{statusLabels.maintenance}</option>
                <option value="retired">{statusLabels.retired}</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
            <div>
              <label style={labelSt}>{f.name_en || 'Equipment Name (English)'} <span style={{ color: '#f87171' }}>*</span></label>
              <input type="text" value={form.name} onChange={(e) => handleChange('name', e.target.value)} style={inputSt} placeholder={f.name_en_placeholder || 'e.g. Split AC - Reception'} required />
            </div>
            <div>
              <label style={labelSt}>{f.name_ar || 'Equipment Name (Arabic)'}</label>
              <input type="text" value={form.name_ar} onChange={(e) => handleChange('name_ar', e.target.value)} style={{ ...inputSt, direction: 'rtl' }} placeholder="مكيف سبليت - الاستقبال" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '16px' }}>
            <div>
              <label style={labelSt}>{f.brand || 'Brand'}</label>
              <input type="text" value={form.brand} onChange={(e) => handleChange('brand', e.target.value)} style={inputSt} placeholder={f.brand_placeholder || 'e.g. Carrier'} />
            </div>
            <div>
              <label style={labelSt}>{f.model || 'Model'}</label>
              <input type="text" value={form.model} onChange={(e) => handleChange('model', e.target.value)} style={inputSt} placeholder={f.model_placeholder || 'e.g. 42QHC024'} />
            </div>
            <div>
              <label style={labelSt}>{f.serial_number || 'Serial Number'}</label>
              <input type="text" value={form.serial_number} onChange={(e) => handleChange('serial_number', e.target.value)} style={inputSt} placeholder={f.serial_placeholder || 'e.g. CAR2024001'} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
            <div>
              <label style={labelSt}>{f.installation_date || 'Installation Date'}</label>
              <input type="date" value={form.installation_date} onChange={(e) => handleChange('installation_date', e.target.value)} style={inputSt} />
            </div>
            <div>
              <label style={labelSt}>{f.warranty_expiry || 'Warranty Expiry'}</label>
              <input type="date" value={form.warranty_expiry} onChange={(e) => handleChange('warranty_expiry', e.target.value)} style={inputSt} />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div style={sectionSt}>
          <label style={labelSt}>{f.notes || 'Notes'}</label>
          <textarea value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} style={{ ...inputSt, minHeight: '80px', resize: 'vertical' }} placeholder={f.notes_placeholder || 'Internal notes about this equipment...'} />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
          <button type="button" onClick={() => navigate(-1)} style={{
            backgroundColor: 'transparent', color: '#94a3b8', border: '2px solid rgba(255,255,255,0.15)',
            padding: '10px 20px', borderRadius: '8px', fontWeight: '600', fontSize: '14px', cursor: 'pointer'
          }}>{f.cancel || 'Cancel'}</button>
          <button type="submit" disabled={saving} style={{
            backgroundColor: saving ? '#4b5563' : '#007bff',
            color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '8px',
            fontWeight: '600', fontSize: '14px', cursor: saving ? 'wait' : 'pointer'
          }}>{saving ? (f.saving || 'Saving...') : (isEdit ? (f.edit_equipment || 'Update Equipment') : (f.new_equipment || 'Register Equipment'))}</button>
        </div>
      </form>
    </div>
    </Layout>
  );
};

export default EquipmentForm;